import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { db } from '../utils/firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { validateWeight } from '../utils/activities';
import { calculateBMIForRecord } from '../utils/bodyMetrics';
import logger from '../utils/logger';
import { toMillis } from '../utils/timestamps';
import ModalBackdrop from './ModalBackdrop';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

function WeightTracking({ userId, profileHeight, onCurrentWeightChange, onError }) {
  const [weightRecords, setWeightRecords] = useState([]);
  const [newWeight, setNewWeight] = useState('');
  const [selectedWeightRecord, setSelectedWeightRecord] = useState(null);
  const [editWeight, setEditWeight] = useState('');
  const [showAllWeightRecordsPopup, setShowAllWeightRecordsPopup] = useState(false);
  const [loadingWeightRecords, setLoadingWeightRecords] = useState(false);

  useEffect(() => {
    if (!userId) return;

    const fetchWeightRecords = async () => {
      setLoadingWeightRecords(true);
      try {
        const querySnapshot = await getDocs(collection(db, 'users', userId, 'weightRecords'));
        const weightData = querySnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .sort((a, b) => toMillis(b.timestamp) - toMillis(a.timestamp));
        setWeightRecords(weightData);
      } catch (error) {
        logger.error(error, { page: 'User', action: 'fetchWeightRecords' });
        onError('Failed to fetch weight records. Please try again.');
      } finally {
        setLoadingWeightRecords(false);
      }
    };

    fetchWeightRecords();
  }, [userId, onError]);

  const handleWeightSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateWeight(newWeight);
    if (validationError) {
      onError(validationError);
      return;
    }
    const weightValue = parseFloat(newWeight);
    const nowMs = Date.now();
    const weightRecord = { weight: weightValue };
    try {
      const docRef = await addDoc(collection(db, 'users', userId, 'weightRecords'), {
        ...weightRecord,
        timestamp: serverTimestamp(),
      });
      await setDoc(doc(db, 'users', userId), { currentWeight: weightValue }, { merge: true });
      setWeightRecords([{ id: docRef.id, ...weightRecord, timestamp: nowMs }, ...weightRecords].sort((a, b) => toMillis(b.timestamp) - toMillis(a.timestamp)));
      onCurrentWeightChange(weightValue.toString());
      setNewWeight('');
    } catch (error) {
      logger.error(error, { page: 'User', action: 'saveWeight' });
      onError('Failed to log weight. Please try again.');
    }
  };

  const handleEditWeightSubmit = async (e) => {
    e.preventDefault();
    if (!selectedWeightRecord) return;
    const weightValue = parseFloat(editWeight);
    if (isNaN(weightValue) || weightValue <= 0) {
      onError('Weight must be a positive number.');
      return;
    }
    try {
      const weightRef = doc(db, 'users', userId, 'weightRecords', selectedWeightRecord.id);
      await updateDoc(weightRef, { weight: weightValue });
      const querySnapshot = await getDocs(collection(db, 'users', userId, 'weightRecords'));
      const weightData = querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => toMillis(b.timestamp) - toMillis(a.timestamp));
      setWeightRecords(weightData);
      if (weightData[0].id === selectedWeightRecord.id) {
        await setDoc(doc(db, 'users', userId), { currentWeight: weightValue }, { merge: true });
        onCurrentWeightChange(weightValue.toString());
      } else {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          onCurrentWeightChange(userData.currentWeight?.toString() || '');
        }
      }
      setSelectedWeightRecord(null);
      setEditWeight('');
    } catch (error) {
      logger.error(error, { page: 'User', action: 'editWeight' });
      onError('Failed to update weight record. Please try again.');
    }
  };

  const handleDeleteWeight = async () => {
    if (!selectedWeightRecord) return;
    try {
      const weightRef = doc(db, 'users', userId, 'weightRecords', selectedWeightRecord.id);
      await deleteDoc(weightRef);
      const querySnapshot = await getDocs(collection(db, 'users', userId, 'weightRecords'));
      const weightData = querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => toMillis(b.timestamp) - toMillis(a.timestamp));
      setWeightRecords(weightData);
      const newCurrentWeight = weightData.length > 0 ? weightData[0].weight : 0;
      await setDoc(doc(db, 'users', userId), { currentWeight: newCurrentWeight }, { merge: true });
      onCurrentWeightChange(newCurrentWeight.toString());
      setSelectedWeightRecord(null);
      setEditWeight('');
    } catch (error) {
      logger.error(error, { page: 'User', action: 'deleteWeight' });
      onError('Failed to delete weight record. Please try again.');
    }
  };

  const chartData = {
    labels: weightRecords
      .sort((a, b) => toMillis(a.timestamp) - toMillis(b.timestamp))
      .map(record => new Date(toMillis(record.timestamp)).toLocaleDateString()),
    datasets: [
      {
        label: 'Weight (lbs)',
        data: weightRecords
          .sort((a, b) => toMillis(a.timestamp) - toMillis(b.timestamp))
          .map(record => record.weight),
        fill: false,
        borderColor: '#f97316',
        backgroundColor: '#f97316',
        tension: 0.1,
        pointRadius: 4,
        yAxisID: 'y',
      },
      {
        label: 'BMI',
        data: weightRecords
          .sort((a, b) => toMillis(a.timestamp) - toMillis(b.timestamp))
          .map(record => calculateBMIForRecord(record.weight, profileHeight))
          .filter(bmi => bmi !== null),
        fill: false,
        borderColor: '#22c55e',
        backgroundColor: '#22c55e',
        tension: 0.1,
        pointRadius: 4,
        yAxisID: 'y1',
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true },
      title: {
        display: true,
        text: 'Weight/BMI Over Time',
        color: '#f1f5f9',
        font: { size: 16 }
      },
      tooltip: {
        backgroundColor: '#0f172a',
        titleColor: '#f1f5f9',
        bodyColor: '#f1f5f9',
        borderColor: '#334155',
        borderWidth: 1
      }
    },
    scales: {
      x: {
        title: { display: true, text: 'Date', color: '#94a3b8' },
        ticks: { color: '#94a3b8' },
        grid: { color: '#334155' }
      },
      y: {
        title: { display: true, text: 'Weight (lbs)', color: '#f97316' },
        ticks: { color: '#94a3b8' },
        beginAtZero: false,
        position: 'left',
        grid: { color: '#334155' }
      },
      y1: {
        title: { display: true, text: 'BMI', color: '#22c55e' },
        ticks: { color: '#22c55e' },
        beginAtZero: false,
        position: 'right',
        grid: { drawOnChartArea: false }
      }
    }
  };

  return (
    <>
      <div className="weight-tracking">
        <div className="recent-activities-header">
          <h3>Weight Tracking</h3>
          <span
            className="show-all-link"
            onClick={() => setShowAllWeightRecordsPopup(true)}
          >
            Show All
          </span>
        </div>
        <p className="activity-instruction">Select a record to modify or delete.</p>
        <form onSubmit={handleWeightSubmit} className="weight-input">
          <input
            type="number"
            value={newWeight}
            onChange={(e) => setNewWeight(e.target.value)}
            placeholder="Weight"
          />
          <button type="submit" className="btn-primary">Log</button>
        </form>
        {loadingWeightRecords ? (
          <p className="loading">Loading...</p>
        ) : weightRecords.length === 0 ? (
          <div className="empty-state">
            <svg className="empty-state-icon" xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
            </svg>
            <p className="empty-state-headline">No weight logged yet</p>
            <p className="empty-state-subtitle">Enter your current weight above to start tracking</p>
          </div>
        ) : (
          <div className="data-table data-table--weight">
            <div className="data-table-header">
              <span>Date</span><span>Weight</span>
            </div>
            {weightRecords
              .sort((a, b) => toMillis(b.timestamp) - toMillis(a.timestamp))
              .slice(0, 5)
              .map(record => (
                <div
                  key={record.id}
                  className="data-table-row"
                  onClick={() => {
                    setSelectedWeightRecord(record);
                    setEditWeight(record.weight.toString());
                  }}
                >
                  <span className="data-table-cell" data-label="Date">{new Date(toMillis(record.timestamp)).toLocaleDateString()}</span>
                  <span className="data-table-cell" data-label="Weight">{record.weight} lbs</span>
                </div>
              ))}
          </div>
        )}
        {weightRecords.length >= 2 && profileHeight && (
          <div className="weight-chart">
            <Line data={chartData} options={chartOptions} />
          </div>
        )}
      </div>
      {selectedWeightRecord && (
        <ModalBackdrop onClose={() => setSelectedWeightRecord(null)}>
          <div className="popup-content">
            <h3>Weight Record Details</h3>
            <form onSubmit={handleEditWeightSubmit}>
              <p className="activity-date">{new Date(toMillis(selectedWeightRecord.timestamp)).toLocaleDateString()}</p>
              <label>
                Weight (lbs):
                <input
                  type="number"
                  value={editWeight}
                  onChange={(e) => setEditWeight(e.target.value)}
                />
              </label>
              <div className="button-group">
                <button type="submit" className="btn-primary">Save</button>
                <button type="button" className="btn-danger" onClick={handleDeleteWeight}>Delete</button>
                <button type="button" className="btn-secondary" onClick={() => setSelectedWeightRecord(null)}>Close</button>
              </div>
            </form>
          </div>
        </ModalBackdrop>
      )}
      {showAllWeightRecordsPopup && (
        <ModalBackdrop onClose={() => setShowAllWeightRecordsPopup(false)}>
          <div className="popup-content all-activities-popup">
            <div className="popup-header">
              <h3>All Weight Records</h3>
              <button
                className="popup-close-button btn-secondary"
                onClick={() => setShowAllWeightRecordsPopup(false)}
              >
                Close
              </button>
            </div>
            <p className="activity-instruction">Select a record to modify or delete.</p>
            {weightRecords.length === 0 ? (
              <p className="no-data">No weight records yet.</p>
            ) : (
              <div className="data-table data-table--weight">
                <div className="data-table-header">
                  <span>Date</span><span>Weight</span>
                </div>
                {weightRecords
                  .sort((a, b) => toMillis(b.timestamp) - toMillis(a.timestamp))
                  .map(record => (
                    <div
                      key={record.id}
                      className="data-table-row"
                      onClick={() => {
                        setSelectedWeightRecord(record);
                        setEditWeight(record.weight.toString());
                      }}
                    >
                      <span className="data-table-cell" data-label="Date">{new Date(toMillis(record.timestamp)).toLocaleDateString()}</span>
                      <span className="data-table-cell" data-label="Weight">{record.weight} lbs</span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </ModalBackdrop>
      )}
    </>
  );
}

export default WeightTracking;
